import React, { useState, useEffect } from 'react';
import {
  ImageIcon,
  Video,
  Bot,
  BarChart3,
  Sparkles,
  Clock,
} from 'lucide-react';
import Sidebar from '../../components/sidebar/Sidebar';
import RecentWorkDetailModal from '../../components/recent-works/RecentWorkDetailModal';
import { useLogout } from '../../hooks/useLogout';
import {
  fetchRecentWorks,
  RecentWorkItem,
  RecentWorkType,
} from '../../services/api/recentWorksService';
import {
  TaskyButton,
  TaskyCard,
  TaskyEmptyState,
  TaskyErrorState,
  TaskySpinner,
  ResponsiveAppShell,
  TaskyTopBar,
} from '../../components/tasky';

const PER_PAGE = 50;

const typeToLabel: Record<RecentWorkType, string> = {
  chat: 'AIチャット',
  image: '画像生成',
  video: '動画広告',
  manga: '漫画広告',
  lp: 'LP制作',
  music: '音楽生成',
};

const typeToIcon: Record<RecentWorkType, { icon: React.ReactNode; iconBg: string; iconColor: string }> = {
  chat: { icon: <Bot className="w-4 h-4" />, iconBg: 'bg-primary/10', iconColor: 'text-primary' },
  image: { icon: <ImageIcon className="w-4 h-4" />, iconBg: 'bg-primary/10', iconColor: 'text-primary' },
  video: { icon: <Video className="w-4 h-4" />, iconBg: 'bg-purple/10', iconColor: 'text-purple' },
  manga: { icon: <BarChart3 className="w-4 h-4" />, iconBg: 'bg-success/10', iconColor: 'text-success' },
  lp: { icon: <Sparkles className="w-4 h-4" />, iconBg: 'bg-success/10', iconColor: 'text-success' },
  music: { icon: <Sparkles className="w-4 h-4" />, iconBg: 'bg-purple/10', iconColor: 'text-purple' },
};

const formatRelativeTime = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return '今';
  if (m < 60) return `${m}分前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}時間前`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}日前`;
  return new Date(iso).toLocaleDateString('ja-JP');
};

const RecentWorksPage: React.FC = () => {
  const handleLogout = useLogout();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [items, setItems] = useState<RecentWorkItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedWork, setSelectedWork] = useState<RecentWorkItem | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    fetchRecentWorks(currentPage, PER_PAGE)
      .then((res) => {
        setItems(res.data || []);
        setLastPage(res.last_page || 1);
        setTotal(res.total || 0);
      })
      .catch((err) => {
        console.error('Failed to load recent works:', err);
        setError('最近の作業の読み込みに失敗しました。');
      })
      .finally(() => setIsLoading(false));
  }, [currentPage]);

  return (
    <ResponsiveAppShell
      sidebar={(
        <Sidebar
          onLogout={handleLogout}
          onToggleCollapse={setSidebarCollapsed}
          collapsed={sidebarCollapsed}
        />
      )}
      mobileSidebar={(
        <Sidebar
          onLogout={handleLogout}
          onToggleCollapse={() => undefined}
          collapsed={false}
        />
      )}
      topbarTitle="最近の作業"
    >
      <main className="flex min-h-[calc(100dvh-56px)] flex-col overflow-hidden bg-warm-bg font-sans lg:h-screen lg:min-h-0">
        <TaskyTopBar title="最近の作業" icon={<Clock className="w-5 h-5" />} />

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-warm-bg">
          <div className="mx-auto max-w-4xl px-4 py-5 sm:px-6 sm:py-8">
            {total > 0 && <p className="text-sm text-gray-400 mb-6">（全 {total} 件）</p>}

            {isLoading && (
              <div className="flex flex-col items-center gap-2 py-12">
                <TaskySpinner size="md" />
                <span className="text-sm text-gray-500">読み込み中...</span>
              </div>
            )}

            {error && (
              <TaskyErrorState title="読み込みに失敗しました" description={error} className="mb-6" />
            )}

            {!isLoading && !error && items.length === 0 && (
              <TaskyEmptyState
                icon={<Clock />}
                title="まだ作業履歴がありません"
              />
            )}

            {!isLoading && items.length > 0 && (
              <TaskyCard variant="bordered" padding="none" className="overflow-hidden" data-tutorial="recent-works-list">
                <div
                  className="hidden items-center border-b border-warm-border bg-warm-surface px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 sm:grid"
                  style={{ gridTemplateColumns: '32px 1fr 120px 100px 72px' }}
                >
                  <span />
                  <span>プロジェクト名</span>
                  <span>種別</span>
                  <span className="text-right">更新日時</span>
                  <span />
                </div>
                {items.map((item, idx) => {
                  const meta = typeToIcon[item.type] || typeToIcon.chat;
                  return (
                    <button
                      key={`${item.type}-${item.id}`}
                      type="button"
                      onClick={() => setSelectedWork(item)}
                      className={`flex w-full flex-col gap-3 px-4 py-4 text-left transition-colors hover:bg-warm-hover sm:grid sm:items-center sm:py-3 ${
                        idx < items.length - 1 ? 'border-b border-warm-border' : ''
                      }`}
                      style={{ gridTemplateColumns: '32px 1fr 120px 100px 72px', gap: '12px' }}
                    >
                      <div className="flex min-w-0 items-start gap-3 sm:contents">
                        <div className={`w-7 h-7 rounded-tasky-sm ${meta.iconBg} flex items-center justify-center flex-shrink-0 ${meta.iconColor}`} title={typeToLabel[item.type]}>
                          {meta.icon}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-dark truncate">{item.title}</p>
                          <p className="text-xs text-gray-400 truncate">{item.subtitle}</p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">{typeToLabel[item.type]}</span>
                      <p className="text-xs text-gray-400 sm:text-right sm:whitespace-nowrap">
                        {formatRelativeTime(item.updated_at)}
                      </p>
                      <div className="text-left sm:text-right">
                        <span className="text-xs font-semibold text-gray-500 bg-warm-surface border border-warm-border px-2.5 py-1 rounded-full whitespace-nowrap">
                          詳細
                        </span>
                      </div>
                    </button>
                  );
                })}
              </TaskyCard>
            )}

            {lastPage > 1 && (
              <div className="flex justify-center items-center gap-2 mt-10">
                <TaskyButton
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  前へ
                </TaskyButton>
                <span className="px-4 py-2 text-gray-500 text-sm">
                  {currentPage} / {lastPage}
                </span>
                <TaskyButton
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(lastPage, p + 1))}
                  disabled={currentPage === lastPage}
                >
                  次へ
                </TaskyButton>
              </div>
            )}
          </div>
        </div>
      </main>

      <RecentWorkDetailModal item={selectedWork} onClose={() => setSelectedWork(null)} />
    </ResponsiveAppShell>
  );
};

export default RecentWorksPage;
